import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  Card,
  Cell,
  Divider,
  FormField,
  IconButton,
  Image,
  Input,
  Layout,
  Loader,
  MarketingLayout,
  Page,
  SectionHelper,
  Text,
  TextButton,
} from "@wix/design-system";
import { showToast } from "@wix/dashboard-sdk";

import FaireConfig from "../../../config/faire";
import Instance from "../../../utilities/instance";
import FaireContext from "../../../contexts/faire-context";
import * as Icons from "@wix/wix-ui-icons-common";
import "../../../dashboard/pages/settings/page.json";
import Site from "../../../models/site";
import CurrencySettings from "../settings/CurrencySettings";
import InventorySettings from "../settings/InventorySettings";
import OrderSettings from "../settings/OrderSettings";
import IconFaire from "../../../dashboard/images/icon faire connect.svg";
import ApiKeyFaire from "../../../dashboard/images/ApiKeyImage.png";
import ApiKeyChangeModal from "../../common/modal/ApiKeyChangeModal";
import OauthNotConnected from "./OauthNotConnected";

const ConnectFaire = () => {
  /*
  |--------------------------------------------------------------------------
  | Connect using API key
  |--------------------------------------------------------------------------
  |
  */

  const [apiKey, setApiKey] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [instance, setInstance] = React.useState("");
  const [authorizationMode, setAuthorizationMode] = useState("oauth");
  const [currentAuthorizationMode, setCurrentAuthorizationMode] = useState("api-key");
  const [siteDetails, setSiteDetails] = useState([]);

  /*
  |--------------------------------------------------------------------------
  | Handle connect using API key
  |--------------------------------------------------------------------------
  |
  */
  const [showChangeApiKeyConfirmation, setShowChangeApiKeyConfirmation] = useState(false);
  const handleConnectUsingApiKey = () => {
    setIsConnecting(true);
    Site.setFaireAccessToken({
      faire_access_token: apiKey,
    })
      .then((response) => {
        if (response?.key_changed) {
          setIsFaireConnected(false);
          setShowChangeApiKeyConfirmation(true);
        } else {
          setIsFaireConnected(true);
          setCurrentTab("currency-settings");
          showToast({
            type: "success",
            message: response.message,
          });
        }

        setErrorMessage(null);
      })
      .catch((error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsConnecting(false);
      });
  };

  const [isReplacing, setIsReplacing] = useState(false);
  const replaceApiKey = () => {
    setIsReplacing(true);
    Site.replaceFaireAccessToken({
      faire_access_token: apiKey,
    })
      .then((response) => {
        setIsFaireConnected(true);
        setShowChangeApiKeyConfirmation(false);
      })
      .catch((error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsReplacing(false);
        setIsConnecting(false);
      });
  };

  /**
   * Faire setup details
   */
  const { isFaireConnected, setIsFaireConnected } = useContext(FaireContext);
  const { isConversionSet, setIsConversionSet } = useContext(FaireContext);
  const { isInventorySettingSet, setIsInventorySettingSet } = useContext(FaireContext);
  const { isOrderSettingSet, setIsOrderSettingSet } = useContext(FaireContext);
  const { isCurrencyMatched, setIsCurrencyMatched } = useContext(FaireContext);

  const fetchSiteDetails = () => {
    Site.details().then((response) => {
      setSiteDetails(response);
      setIsCurrencyMatched(response?.faire_currency === response?.wix_currency ? true : false);
      setAuthorizationMode(response.authorization_mode);
    });
  };

  const renderCurrencySettingsForm = () => {
    return (
      <CurrencySettings
        context="onboarding"
        onSuccess={() => {
          setIsConversionSet(true);
          setCurrentTab("inventory-settings");
        }}
      />
    );
  };

  const renderInventorySettingsForm = () => {
    return (
      <InventorySettings
        context="onboarding"
        onSuccess={() => {
          setIsInventorySettingSet(true);
          setCurrentTab("order-settings");
        }}
      />
    );
  };

  const renderOrderSettingsForm = () => {
    return (
      <OrderSettings
        context="onboarding"
        onSuccess={() => {
          setIsOrderSettingSet(true);
          window.location.reload();
        }}
      />
    );
  };

  React.useEffect(() => {
    setInstance(Instance.get());
  }, []);

  useEffect(() => {
    fetchSiteDetails();
  }, [isFaireConnected]);

  const [currentTab, setCurrentTab] = React.useState("connect-faire-account");

  const apiKeyChangeModalProps = {
    showChangeApiKeyConfirmation,
    setShowChangeApiKeyConfirmation,
    isConnecting,
    replaceApiKey,
    isReplacing,
  };

  return (
    <Page>
      <Page.Header title="Onboarding" />
      <Page.Content>
        <ApiKeyChangeModal {...apiKeyChangeModalProps} />

        <Layout gap="20px">
          {
            <>
              <Cell>
                {siteDetails?.use_api_key_if_oauth_missing === true && <OauthNotConnected />}
                <Card>
                  {currentAuthorizationMode === "oauth" ? (
                    <MarketingLayout
                      title="1. Connect your Faire account"
                      description="Please link your Faire Account to start Integration & Sync"
                      actions={
                        !isFaireConnected ? (
                          <Box direction="horizontal" gap="10px">
                            <Button
                              as="a"
                              size="small"
                              href={`${FaireConfig.authorizationUrl}&state=${instance}`}
                              target="_blank"
                            >
                              Link Your Faire Store
                            </Button>
                          </Box>
                        ) : (
                          ""
                        )
                      }
                      size="tiny"
                      image={
                        !isFaireConnected ? (
                          <Box width="100%" align="right">
                            <Image width="120px" src={IconFaire} transparent />
                          </Box>
                        ) : (
                          <Icons.Confirm color="green" size={30} />
                        )
                      }
                    />
                  ) : (
                    <>
                      {!isFaireConnected ? (
                        <MarketingLayout
                          title="1. Connect your Faire account"
                          description="Please Enter Your Faire API key to start the linking process"
                          badge={
                            <Image
                              style={{ marginLeft: "30px", marginTop: "20px" }}
                              width="170px"
                              src={IconFaire}
                              transparent
                            />
                          }
                          actions={
                            !isFaireConnected ? (
                              <Box direction="horizontal" gap="10px" width={"100%"}>
                                <FormField
                                  label={<Text weight="bold">Faire API key</Text>}
                                  {...(errorMessage ? { status: "error" } : {})}
                                  statusMessage={errorMessage}
                                >
                                  <Input
                                    size="large"
                                    placeholder="Copy your brand key here."
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                  />
                                  <br />
                                  <Button size="small" priority="primary" onClick={handleConnectUsingApiKey}>
                                    {isConnecting ? <Loader size="tiny" /> : "Connect"}
                                  </Button>
                                </FormField>
                              </Box>
                            ) : (
                              ""
                            )
                          }
                          size="small"
                          image={
                            <Card>
                              <Card.Header
                                title={
                                  <Text weight="bold">
                                    To get the Faire API Key, login into your Faire.com and follow the steps below,{" "}
                                    <br /> or you can refer to this{" "}
                                    <a
                                      target="_blank"
                                      href="https://www.loom.com/share/67ccab89ef854896be2b1abc058ed74a"
                                    >
                                      <u>video</u>
                                    </a>
                                    .
                                  </Text>
                                }
                              ></Card.Header>
                              <Card.Divider />
                              <Card.Content>
                                <Box marginBottom={"11px"} minHeight="120px" direction="vertical">
                                  <Text listStyle="circle">
                                    <Text weight="bold">Steps to get API Key from Faire:</Text>
                                    <ul>
                                      <li>
                                        Go to{" "}
                                        <a href="https://www.faire.com" target="_blank">
                                          Faire.com
                                        </a>{" "}
                                        and sign in to your account
                                      </li>
                                      <li>
                                        After logging in &nbsp;
                                        <a
                                          href="https://www.faire.com/brand-portal/integrations/apa_f3ewv7jhvw"
                                          target="_blank"
                                        >
                                          Click here
                                        </a>
                                        &nbsp; for getting API key
                                      </li>
                                      <li>Your API Key will appear at the bottom right of the screen</li>
                                      <li>
                                        Simply Copy the API Key and paste it into the text box labeled "Faire API Key"
                                      </li>
                                    </ul>
                                  </Text>
                                  <Image width="230px" src={ApiKeyFaire} transparent />
                                </Box>
                                <Divider />
                                <SectionHelper size="small" appearance="danger">
                                  <Box align="center" gap={2}>
                                    <Icons.InfoCircleFilled color="red" size={40} />
                                    <Text>
                                      Our Application only supports sellers who have a Brand registered on Faire.
                                    </Text>
                                  </Box>
                                </SectionHelper>

                                <Box gap={1} padding={"10px"} direction="vertical">
                                  <Text weight="bold">Not Selling on Faire.com yet?</Text>
                                  <Divider />
                                  <Text textAlign="center">
                                    If you dont have a store on Faire.com yet, <br />
                                    please go to their website first and register an account by clicking the given link:
                                    &nbsp; <br />
                                    <a href="https://www.faire.com/brands" target="_blank">
                                      Faire.com/brands
                                    </a>
                                  </Text>
                                </Box>

                                <Box gap={1} padding={"10px"} direction="vertical">
                                  <div
                                    style={{
                                      fontSize: "16px",
                                      backgroundColor: "rgba(55, 160, 212, 0.1)",
                                      padding: "15px",
                                      borderRadius: "5px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <Text>
                                      <b>Disclaimer:</b> Registering as a brand on Faire may take up to 30 days.
                                    </Text>
                                  </div>
                                </Box>
                              </Card.Content>
                            </Card>
                          }
                        />
                      ) : (
                        <MarketingLayout
                          title="1. Connect your Faire account"
                          description="Please Enter Your Faire API key to start the linking process"
                          size="tiny"
                          image={
                            <Button priority="primary" disabled={!isFaireConnected} size="small">
                              <Icons.Confirm color="#31c56a" size={30} /> Connected
                            </Button>
                          }
                        />
                      )}
                    </>
                  )}

                  <Divider />
                  {!isFaireConnected && (
                    <Box align="left" padding="12px 30px">
                      <TextButton
                        onClick={() => {
                          currentAuthorizationMode === "oauth"
                            ? setCurrentAuthorizationMode("api-key")
                            : setCurrentAuthorizationMode("oauth");
                        }}
                        size="medium"
                        underline="always"
                        suffixIcon={<Icons.ExternalLinkSmall />}
                      >
                        {currentAuthorizationMode === "oauth"
                          ? "Link your Faire store to Wix using API key manually"
                          : "Link your Faire store to Wix using Oauth"}
                      </TextButton>
                    </Box>
                  )}
                </Card>
              </Cell>

              {!isCurrencyMatched && (
                <Cell>
                  <Card>
                    <MarketingLayout
                      title="2. Currency settings"
                      description="To enable multiple currency between Wix and Faire."
                      size="tiny"
                      image={
                        isFaireConnected ? (
                          !isConversionSet && currentTab !== "currency-settings" ? (
                            <Button
                              disabled={!isFaireConnected}
                              size="small"
                              onClick={() => setCurrentTab("currency-settings")}
                            >
                              Set currency settings
                            </Button>
                          ) : currentTab !== "currency-settings" ? (
                            <>
                              <IconButton
                                style={{ marginRight: "15px" }}
                                size="large"
                                onClick={() => setCurrentTab("currency-settings")}
                              >
                                <Icons.ChevronDown />
                              </IconButton>
                              <Icons.Confirm color="green" size={30} />
                            </>
                          ) : (
                            ""
                          )
                        ) : undefined
                      }
                      actions={currentTab === "currency-settings" ? renderCurrencySettingsForm() : ""}
                    />
                  </Card>
                </Cell>
              )}
              <Cell>
                <Card>
                  <MarketingLayout
                    title={`${!isCurrencyMatched ? "3." : "2."} Inventory settings`}
                    description="Price, quantity, and SKU sync settings between Wix and Faire which are crucial while sharing catalog and orders between Wix and Faire."
                    size="tiny"
                    image={
                      isFaireConnected ? (
                        !isInventorySettingSet && currentTab !== "inventory-settings" ? (
                          <Button
                            disabled={!isFaireConnected}
                            size="small"
                            onClick={() => setCurrentTab("inventory-settings")}
                          >
                            Set inventory settings
                          </Button>
                        ) : currentTab !== "inventory-settings" ? (
                          <>
                            <IconButton
                              style={{ marginRight: "15px" }}
                              size="large"
                              onClick={() => setCurrentTab("inventory-settings")}
                            >
                              <Icons.ChevronDown />
                            </IconButton>
                            <Icons.Confirm color="green" size={30} />
                          </>
                        ) : (
                          ""
                        )
                      ) : undefined
                    }
                    actions={currentTab === "inventory-settings" ? renderInventorySettingsForm() : ""}
                  />
                </Card>
              </Cell>
              <Cell>
                <Card>
                  <MarketingLayout
                    title={`${!isCurrencyMatched ? "4." : "3."} Order settings`}
                    description="Order sync and display setting between Wix and Faire."
                    size="tiny"
                    image={
                      isFaireConnected ? (
                        !isOrderSettingSet && currentTab !== "order-settings" ? (
                          <Button
                            disabled={!isFaireConnected}
                            size="small"
                            onClick={() => setCurrentTab("order-settings")}
                          >
                            Set order settings
                          </Button>
                        ) : currentTab !== "order-settings" ? (
                          <>
                            <IconButton
                              style={{ marginRight: "15px" }}
                              size="large"
                              onClick={() => setCurrentTab("order-settings")}
                            >
                              <Icons.ChevronDown />
                            </IconButton>
                            <Icons.Confirm color="green" size={30} />
                          </>
                        ) : (
                          ""
                        )
                      ) : undefined
                    }
                    actions={currentTab === "order-settings" ? renderOrderSettingsForm() : ""}
                  />
                </Card>
              </Cell>
            </>
          }
        </Layout>
      </Page.Content>
    </Page>
  );
};

export default ConnectFaire;
